import request from "@/tool/request";
import API from "@/tool/api";
import { getResultMessage, unwrapResult } from "@/tool/result";
import appointmentContract from "@/tool/photographyAppointments.cjs";
import qs from "qs";

const {
    createPhotographyAppointmentCard,
    getAppointmentStatuses,
    normalizePhotographyAppointmentDetail,
    normalizePhotographyAppointmentPage,
    normalizeAppointmentRefunds,
    normalizeCancellationPreview,
    normalizeCancellationResult,
} = appointmentContract;
const { SERVICE, APPOINTMENT, END_REASON } = appointmentContract.STATUS_CODES;

const initialState = () => ({
    status: "idle",
    error: "",
    items: [],
    selected: null,
    detailRequestId: 0,
    listRequestId: 0,
    total: 0,
    pageNo: 1,
    pageSize: 10,
    group: "all",
});

const loadPages = (status, params, pageNo = 1, items = []) => request.get(API.photography.appointments, {
    params: {
        ...params,
        ...(status ? { status } : {}),
        pageNo,
        pageSize: 50,
    },
}).then((response) => {
    const page = normalizePhotographyAppointmentPage(unwrapResult(response, "摄影预约列表加载失败"));
    const combined = items.concat(page.items);
    if (!page.items.length && combined.length < page.total) throw new Error("预约列表已变化，请重新加载");
    return combined.length < page.total ? loadPages(status, params, pageNo + 1, combined) : combined;
});

export default {
    state: initialState(),
    mutations: {
        clearUser(state) {
            const detailRequestId = state.detailRequestId + 1;
            const listRequestId = state.listRequestId + 1;
            Object.assign(state, initialState(), { detailRequestId, listRequestId });
        },
        clearPhotographyAppointmentsList(state) { state.listRequestId += 1; state.items = []; state.total = 0; state.status = "idle"; state.error = ""; },
        startPhotographyAppointmentsLoad(state, criteria) {
            state.listRequestId += 1;
            state.items = []; state.total = 0;
            state.status = "loading";
            state.error = "";
            state.group = criteria.group || "all";
        },
        finishPhotographyAppointmentsLoad(state, { page, requestId }) {
            if (requestId !== state.listRequestId) return;
            Object.assign(state, page, { status: page.items.length ? "ready" : "empty", error: "" });
        },
        failPhotographyAppointmentsLoad(state, { message, requestId }) {
            if (requestId !== state.listRequestId) return;
            state.status = "error";
            state.error = message;
            state.items = [];
            state.total = 0;
        },
        startPhotographyAppointmentDetailLoad(state) {
            state.detailRequestId += 1;
        },
        setPhotographyAppointmentDetail(state, { detail, requestId }) {
            if (requestId === state.detailRequestId) state.selected = detail;
        },
        clearPhotographyAppointmentSelection(state) {
            state.detailRequestId += 1;
            state.selected = null;
        },
    },
    actions: {
        loadUserCompletion(_, serviceOrderNo) {
            return request.get(API.photography.userFulfillment(serviceOrderNo), { headers: { "Cache-Control": "no-store" } })
                .then(response => unwrapResult(response, "履约信息读取失败"));
        },
        async confirmUserCompletion({ dispatch }, { serviceOrderNo, completionConfirmed }) {
            if (completionConfirmed !== true) throw new Error("请先确认结束现场服务");
            const facts = await dispatch("loadUserCompletion", serviceOrderNo);
            if (facts.finishedAt && [SERVICE.AWAITING_PHOTOS, SERVICE.COMPLETED].includes(facts.serviceStatus)) return facts;
            if (facts.serviceStatus !== SERVICE.IN_SERVICE || !facts.checkedInAt || facts.canConfirmCompletion !== true || facts.unresolvedDispute) {
                throw new Error("当前不可确认完成，请刷新履约状态；未解决争议请通过争议处理流程核实");
            }
            return request.post(API.photography.finishUserServiceOrder(serviceOrderNo), qs.stringify({ completionConfirmed: true }))
                .then(response => unwrapResult(response, "现场完成确认失败"));
        },
        loadPhotographyAppointments({ commit, state }, criteria = {}) {
            const group = criteria.group || "all";
            const pageNo = Math.max(1, Number(criteria.pageNo) || 1);
            const pageSize = Math.min(20, Math.max(1, Number(criteria.pageSize) || 8));
            const params = {};
            if (criteria.startDate) params.startDate = criteria.startDate;
            if (criteria.endDate) params.endDate = criteria.endDate;
            const statuses = getAppointmentStatuses(group);
            commit("startPhotographyAppointmentsLoad", { ...criteria, group });
            const requestId = state.listRequestId;
            return Promise.all((statuses.length ? statuses : [null]).map((status) => loadPages(status, params)))
                .then((pages) => pages.reduce((items, page) => items.concat(page), []))
                .then((items) => items.sort((left, right) => (
                    right.createdAt.localeCompare(left.createdAt) || right.appointmentNo.localeCompare(left.appointmentNo)
                )))
                .then((items) => {
                    const start = (pageNo - 1) * pageSize;
                    const visible = items.slice(start, start + pageSize);
                    const cards = visible.map((summary) => createPhotographyAppointmentCard(summary));
                    return { items: cards, total: items.length, pageNo, pageSize };
                }).then((page) => {
                    commit("finishPhotographyAppointmentsLoad", { page, requestId });
                    return page;
                }).catch((error) => {
                    commit("failPhotographyAppointmentsLoad", { message: getResultMessage(error, "摄影预约列表加载失败，请稍后重试"), requestId });
                    throw error;
                });
        },
        loadPhotographyAppointmentDetail({ commit, state }, appointmentNo) {
            commit("startPhotographyAppointmentDetailLoad");
            const requestId = state.detailRequestId;
            return request.get(API.photography.appointmentDetail(appointmentNo)).then(async (response) => {
                const raw = unwrapResult(response, "摄影预约详情加载失败");
                const detail = normalizePhotographyAppointmentDetail(raw);
                await Promise.all(detail.services.filter(service => service.serviceType === 'PHOTOGRAPHY'
                    && ([SERVICE.AWAITING_PHOTOS, SERVICE.COMPLETED].includes(service.serviceStatus)
                        || (service.serviceStatus===SERVICE.ENDED && [END_REASON.RETOUCH_SELECTION_EXPIRED,END_REASON.RETOUCH_UNDELIVERABLE].includes(service.endReason)))).map(async service => {
                    try {
                        const summary = unwrapResult(await request.get(API.photography.userRetouch(service.serviceOrderNo), {
                            headers: { 'Cache-Control': 'no-store' },
                        }), '待出片进度读取失败');
                        if (!summary || !Number.isInteger(summary.stage) || summary.stage < 0 || summary.stage > 9
                            || !Number.isInteger(summary.currentVersionNo) || summary.currentVersionNo < 0 || summary.currentVersionNo > 3
                            || typeof summary.paused !== 'boolean') throw new Error('待出片进度暂不可用');
                        service.retouch = summary;
                    } catch (error) {
                        service.retouchError = getResultMessage(error, '待出片进度读取失败，请刷新重试');
                    }
                }));
                commit("setPhotographyAppointmentDetail", { detail, requestId });
                return detail;
            });
        },
        async loadPhotographyAppointmentAction({ dispatch }, { appointmentNo, action, disclosureAccepted = false }) {
            const detail = await dispatch("loadPhotographyAppointmentDetail", appointmentNo);
            const service = detail.services.find((item) => item.serviceOrderNo === action.targetResourceId);
            if (action.code === "VIEW_SERVICE_CODE" && action.targetResourceType === "SERVICE_ORDER" && service) {
                if (!service.serviceCodeReady) throw new Error('摄影主服务和当前服务均有承接者后才能查看服务码，请刷新状态');
                const response = await request.get(API.photography.userServiceCode(service.serviceOrderNo), {
                    headers: { "Cache-Control": "no-store" },
                });
                const serviceCode = unwrapResult(response, "服务码读取失败");
                if (typeof serviceCode !== "string" || !/^\d{6}$/.test(serviceCode)) throw new Error("服务码暂不可用，请刷新后重试");
                return { ...action, serviceCode, serviceOrderNo: service.serviceOrderNo, roleLabel: service.roleLabel };
            }
            if (action.code === "CONTACT_CREATOR" && action.targetResourceType === "SERVICE_ORDER" && service) {
                const response = await request.post(API.photography.userServiceContact(service.serviceOrderNo), null, {
                    params: { disclosureAccepted: disclosureAccepted === true },
                    headers: { "Cache-Control": "no-store" },
                });
                const contact = unwrapResult(response, "联系请求被拒绝");
                if (disclosureAccepted === true) {
                    if (!contact || !/^tel:\+?[0-9]{3,20}$/.test(contact.actionUrl)) throw new Error("拨号入口暂不可用，请重试");
                    return { ...action, actionUrl: contact.actionUrl };
                }
                if (!contact || typeof contact.disclosureNotice !== "string" || !contact.disclosureNotice.trim()) throw new Error("联系告知暂不可用，请重试");
                return { ...action, disclosureNotice: contact.disclosureNotice, serviceOrderNo: service.serviceOrderNo };
            }
            if (action.code === "PAY" && action.targetResourceId === appointmentNo) {
                return { ...action, route: { name: "photographyAppointmentPayment", params: { appointmentNo } } };
            }
            if (action.code === "CANCEL_APPOINTMENT" && action.targetResourceType === "APPOINTMENT"
                && action.targetResourceId === appointmentNo) {
                if (detail.appointmentStatus === APPOINTMENT.WAITING_PAYMENT) return { ...action, cancellationKind: "UNPAID" };
                const response = await request.get(API.photography.appointmentCancellationOverviewPreview(appointmentNo));
                const preview = normalizeCancellationPreview(unwrapResult(response, "取消预览加载失败"), appointmentNo);
                return { ...action, code: preview.lossless ? "CANCEL_APPOINTMENT_LOSSLESS" : action.code,
                    cancellationKind: "PAID", serviceOrderNos: preview.serviceOrderNos, preview };
            }
            if (action.code === "CANCEL_SERVICE" && action.targetResourceType === "SERVICE_ORDER" && service) {
                const serviceOrderNos = [service.serviceOrderNo];
                const response = await request.get(API.photography.appointmentCancellationPreview(appointmentNo), {
                    params: { serviceOrderNos }, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                });
                const preview = normalizeCancellationPreview(unwrapResult(response, "取消预览加载失败"), appointmentNo);
                return { ...action, cancellationKind: "PAID", serviceOrderNos, preview };
            }
            if (action.code === "VIEW_REFUND" && action.targetResourceType === "APPOINTMENT_REFUNDS"
                && action.targetResourceId === appointmentNo) {
                const response = await request.get(API.photography.appointmentRefunds(appointmentNo));
                const refunds = normalizeAppointmentRefunds(unwrapResult(response, "退款记录加载失败"));
                return { ...action, items: refunds };
            }
            if (action.code === "VIEW_DISPUTE" && action.targetResourceType === "DISPUTE"
                && detail.activeDisputes.some((record) => record.disputeNo === action.targetResourceId)) {
                const response = await request.get(API.photography.appointmentDispute(action.targetResourceId));
                return { ...action, dispute: unwrapResult(response, "处理进度加载失败") };
            }
            if (["REPORT_CREATOR_NO_SHOW", "CREATE_DISPUTE", "CREATE_AFTER_SALES"].includes(action.code) && service && action.targetResourceType === "SERVICE_ORDER") {
                const existing = service.activeDisputes[0];
                if (!existing && !service.actions.some((entry) => entry.code === action.code)) throw new Error("服务状态已变化，请按最新状态操作");
                return { ...action, disputeTarget: {
                    serviceOrderNo: service.serviceOrderNo, roleLabel: service.roleLabel,
                    serviceStartAt: service.serviceStartAt, serviceEndAt: service.serviceEndAt, shootAt: detail.shootAt,
                    mode: action.code === "REPORT_CREATOR_NO_SHOW" ? "CREATOR_NO_SHOW" : action.code === "CREATE_AFTER_SALES" ? "AFTER_SALES" : "OTHER",
                    disputeNo: existing ? existing.disputeNo : undefined,
                } };
            }
            // After-sales initiation remains the separate #48 boundary.
            const pending = { CREATE_AFTER_SALES: "售后申请" };
            if (pending[action.code] && ((action.targetResourceType === "APPOINTMENT" && action.targetResourceId === appointmentNo)
                || (action.targetResourceType === "SERVICE_ORDER" && service))) {
                return { ...action, serviceOrderNo: service ? service.serviceOrderNo : "", pendingMessage: `${pending[action.code]}功能尚未接通，请稍后重试。未提交任何操作。` };
            }
            throw new Error("当前版本暂不支持此操作");
        },
        async submitPhotographyAppointmentCancellation(context, { appointmentNo, idempotencyKey, targetResourceType, serviceOrderNos, cancellationKind }) {
            if (cancellationKind === "UNPAID") {
                const response = await request.post(API.photography.cancelUnpaidPhotographyAppointment(appointmentNo), null);
                unwrapResult(response, "取消待支付预约失败，请刷新状态");
                return { unpaid: true };
            }
            if (targetResourceType === "SERVICE_ORDER") {
                const response = await request.post(API.photography.appointmentCancellations(appointmentNo), { serviceOrderNos },
                    { headers: { "Idempotency-Key": idempotencyKey, "Content-Type": "application/json" } });
                return normalizeCancellationResult(unwrapResult(response, "取消提交失败，请稍后重试"));
            }
            // The appointment endpoint derives affected services and amounts on the server.
            const response = await request.post(API.photography.cancelPhotographyAppointment(appointmentNo), null,
                { headers: { "Idempotency-Key": idempotencyKey } });
            return normalizeCancellationResult(unwrapResult(response, "取消提交失败，请稍后重试"));
        },
        async loadPhotographyAppointmentCancellationResult(context, { appointmentNo, idempotencyKey }) {
            const response = await request.get(API.photography.appointmentCancellationResult(appointmentNo, idempotencyKey));
            return normalizeCancellationResult(unwrapResult(response, "取消结果查询失败"));
        },
        clearPhotographyAppointmentDetail({ commit }) {
            commit("clearPhotographyAppointmentSelection");
        },
    },
};
